import { Box, Typography } from '@mui/material';
import { useI18nContext } from '@procore/core-react';
import Skeleton from '@mui/material/Skeleton';
import React, { useCallback, useMemo } from 'react';
import { PaginationDataSet } from '../../types/pagination';
import { FilterState } from '../../helpers/filters';
import { GeoInfo } from '../../services/geo-info';
import { Business } from '../../types/business';
import BreadcrumbContainer, {
  BreadcrumbItem,
} from '../Breadcrumbs/BreadcrumbContainer';
import SearchPagination from './SearchPagination';
import SearchRadiusSelector from './SearchRadiusSelector';
import { useStyles } from './SearchResults.styles';
import SearchResultsNoResults from './SearchResultsNoResults';
import SearchResultsResultsContainer from './SearchResultsResultsContainer';
import SearchSortBySelector from './SearchSortBySelector';
import SearchResultsLayout from './SearchResultsLayout';
import { FiltersLists } from '../../services/business-registry';

interface SearchResultsProps {
  searchResults?: Business[];
  geoInfo: GeoInfo;
  breadcrumbs?: BreadcrumbItem[];
  useFilterHandlers: FilterState;
  title: string;
  count?: number;
  setPaginationData: (i: PaginationDataSet) => void;
  paginationData: PaginationDataSet;
  isLoadingResults: boolean;
  showSimilarResults?: boolean;
  filtersLists: FiltersLists;
}

const SearchResults = ({
  searchResults = [],
  geoInfo,
  breadcrumbs = [],
  useFilterHandlers,
  title,
  count = 10,
  setPaginationData,
  paginationData,
  isLoadingResults,
  showSimilarResults = false,
  filtersLists,
}: SearchResultsProps) => {
  const classes = useStyles();
  const mbOfSearch = 6;
  const I18n = useI18nContext();
  const { updateSortBy } = useFilterHandlers;

  const onChangeRadiusSelector = useCallback(
    (value) => {
      useFilterHandlers.updateInput({
        ...useFilterHandlers.filters.radius,
        value,
      });
    },
    [useFilterHandlers.updateInput]
  );

  const radiusEnabled = useMemo(() => {
    const radiusFilters = useFilterHandlers?.filters?.radius;
    return !radiusFilters?.disabled && radiusFilters?.value;
  }, [useFilterHandlers?.filters?.radius]);

  const LoadableComponent = useCallback(
    ({ height, width, children, ...rest }) => {
      return isLoadingResults ? (
        <Skeleton height={height} width={width} animation="wave" {...rest} />
      ) : (
        children
      );
    },
    [isLoadingResults]
  );

  const header = useMemo(
    () => (
      <Box pt={3}>
        <BreadcrumbContainer crumbs={breadcrumbs} />
        <Box mt={1}>
          <Typography variant="h1" data-test-id="header-title">
            {title}
          </Typography>
        </Box>
      </Box>
    ),
    [breadcrumbs, title]
  );

  const resultsCountRadiusSelectorContainer = useMemo(
    () => (
      <>
        <Box
          display="flex"
          alignItems="center"
          data-test-id="search-results-count"
        >
          <LoadableComponent
            height={60}
            width={135}
            style={{ marginTop: '-5px' }}
          >
            <Typography
              component="h2"
              variant="h3"
              data-test-id="search-count-text"
            >
              <span
                style={{ fontSize: '14px', fontWeight: 400 }}
                suppressHydrationWarning
              >
                {I18n.t(
                  `components.search_results${
                    count === 1 ? '.singular' : '.plural'
                  }${radiusEnabled ? '.count_within' : '.count'}`,
                  {
                    count: count.toLocaleString(),
                  }
                )}
              </span>
            </Typography>
          </LoadableComponent>

          {radiusEnabled && (
            <Box ml={2}>
              <SearchRadiusSelector
                onChange={onChangeRadiusSelector}
                value={useFilterHandlers.filters?.radius?.value}
              />
            </Box>
          )}
        </Box>
      </>
    ),
    [
      isLoadingResults,
      radiusEnabled,
      count,
      onChangeRadiusSelector,
      useFilterHandlers.filters?.radius?.value,
    ]
  );

  const noResultsContainer = useMemo(
    () => (
      <>
        {!isLoadingResults && (count < 1 || showSimilarResults) && (
          <SearchResultsNoResults
            useFilterHandlers={useFilterHandlers}
            location={geoInfo}
          />
        )}
      </>
    ),
    [
      isLoadingResults,
      count,
      showSimilarResults,
      searchResults?.length,
      useFilterHandlers,
      geoInfo,
    ]
  );

  const sortSelector = useMemo(
    () => (
      <>
        {radiusEnabled && count > 1 && (
          <Box
            className={classes.searchSortBySelector}
            data-test-id="sort-selector"
          >
            <SearchSortBySelector
              onChange={updateSortBy}
              value={useFilterHandlers.filters?.sortBy?.label}
            />
          </Box>
        )}
      </>
    ),
    [
      isLoadingResults,
      radiusEnabled,
      count,
      updateSortBy,
      useFilterHandlers.filters?.sortBy?.label,
    ]
  );

  const searchResultsContainer = useMemo(
    () => (
      <>
        {(isLoadingResults || count > 0) && (
          <>
            <SearchResultsResultsContainer
              searchResults={searchResults}
              marginBottom={count > paginationData.pageSize ? 0 : mbOfSearch}
              loading={isLoadingResults}
            />
            {!isLoadingResults && count > paginationData.pageSize && (
              <Box
                display="flex"
                justifyContent="center"
                mt={15}
                mb={mbOfSearch}
              >
                <SearchPagination
                  setPaginationData={setPaginationData}
                  paginationData={paginationData}
                  totalResults={count}
                />
              </Box>
            )}
          </>
        )}
      </>
    ),
    [
      searchResults,
      count,
      paginationData.pageSize,
      isLoadingResults,
      setPaginationData,
      paginationData,
    ]
  );

  return (
    <SearchResultsLayout
      loading={isLoadingResults}
      header={header}
      resultsCountRadiusSelectorContainer={resultsCountRadiusSelectorContainer}
      noResultsContainer={noResultsContainer}
      sortSelector={sortSelector}
      searchResultsContainer={searchResultsContainer}
      useFilterHandlers={useFilterHandlers}
      searchResultsCount={count}
      filtersLists={filtersLists}
      breadcrumbs={breadcrumbs}
    />
  );
};

export default SearchResults;
